import { useMemo, useState } from 'react';
import { useProjectStore } from '../../stores/projectStore';
import type { ChatEventFirestore } from '../../apiService';

/**
 * Research activity checklist — a top-right inspector that shows which research
 * capabilities the current project has actually used, against the full
 * could/should inventory. "Used" is derived entirely from data already in the
 * store (Firestore tool_call / research_progress events + workspace findings &
 * markers), so it lights up retroactively on existing projects.
 *
 * Prototype → live wiring of the design explored in
 * prototypes/research-checklist-panel.html.
 */

type Kind = 'core' | 'required' | 'optional' | 'situational';
type RowState = 'done' | 'pending' | 'available';

interface Item {
  id: string;
  label: string;
  kind: Kind;
  /** exact tool-call names (event.content) that count as this capability */
  tools?: string[];
  /** tool-call name prefixes (e.g. "jupyter_") */
  toolPrefix?: string[];
  /** research_progress phase keys that count as this capability */
  phases?: string[];
  /** custom signal flag name */
  signal?: 'coverage' | 'gaps' | 'markers' | 'nodes';
}

interface Group {
  name: string;
  items: Item[];
}

// The full could/should inventory — 30 items, 5 groups. Premise validation
// intentionally excluded. Tool names map to backend tool identifiers stored as
// the `content` of tool_call events (see save_tool_call in event_persistence.py).
const GROUPS: Group[] = [
  {
    name: 'Research mode',
    items: [
      { id: 'deep-research', label: 'Deep research', kind: 'core', tools: ['research_workflow', 'offer_deep_research', 'accept_deep_research_offer'] },
      { id: 'light-research', label: 'Light research', kind: 'optional', tools: ['light_research_exploration'] },
      { id: 'quick-lookup', label: 'Quick lookup', kind: 'optional', tools: ['quick_lookup', 'get_document_info'] },
      { id: 'hypothesis-panel', label: 'Hypothesis panel', kind: 'optional', tools: ['hypothesis_panel'] },
      { id: 'background-research', label: 'Background research', kind: 'optional', tools: ['check_research_status'] },
    ],
  },
  {
    name: 'Research pipeline',
    items: [
      { id: 'strategy-planning', label: 'Strategy planning', kind: 'situational', phases: ['strategy_panel'] },
      { id: 'discovery-search', label: 'Discovery & search', kind: 'situational', phases: ['discovery', 'research', 'research_wave_1'] },
      { id: 'triage', label: 'Triage', kind: 'situational', phases: ['triage'] },
      { id: 'xref-expansion', label: 'Cross-reference & graph expansion', kind: 'situational', phases: ['investigation'], tools: ['graph_walk'] },
      { id: 'extraction', label: 'Evidence extraction', kind: 'situational', phases: ['extraction', 'extract'] },
      { id: 'structuring', label: 'Structuring (reasoning graph)', kind: 'situational', phases: ['structuring'], signal: 'nodes' },
      { id: 'gap-analysis', label: 'Gap analysis & depth pass', kind: 'situational', phases: ['gap_analysis', 'research_wave_2'] },
      { id: 'source-coverage', label: 'Source-category coverage sweep', kind: 'situational' },
      { id: 'graft', label: 'Graft into existing graph', kind: 'situational' },
    ],
  },
  {
    name: 'Validation & quality',
    items: [
      { id: 'coverage', label: 'Coverage metrics', kind: 'situational', signal: 'coverage' },
      { id: 'citation', label: 'Citation & grounding', kind: 'situational', tools: ['add_marker'], signal: 'markers' },
      { id: 'graph-check', label: 'Structural graph check', kind: 'required', tools: ['validate_graph'] },
      { id: 'report-grounding', label: 'Report grounding check', kind: 'required', tools: ['check_report_grounding'] },
      { id: 'validation-panel', label: 'Validation panel (on request)', kind: 'optional', tools: ['validate_work', 'validate_plan_work', 'validate_model_work'] },
    ],
  },
  {
    name: 'Retrieval & analysis',
    items: [
      { id: 'semantic-search', label: 'Semantic search', kind: 'optional', tools: ['search_content', 'semantic_search', 'search_articles_by_title'] },
      { id: 'concept-lookup', label: 'Concept & definition lookup', kind: 'optional', tools: ['search_concepts', 'get_definition', 'get_concept_map'] },
      { id: 'full-article', label: 'Full-article retrieval', kind: 'optional', tools: ['get_full_article', 'get_content', 'get_document_section'] },
      { id: 'xref-tracing', label: 'Cross-reference tracing', kind: 'optional', tools: ['get_references', 'trace_dependencies'] },
      { id: 'graph-walk', label: 'Graph walk', kind: 'optional', tools: ['graph_walk'] },
      { id: 'calc-formula', label: 'Calculation & formula extraction', kind: 'optional', tools: ['get_calculation', 'get_article_logic'] },
      { id: 'doc-qa', label: 'Document Q&A', kind: 'optional', tools: ['explore', 'explore_document', 'get_document_summary'] },
      { id: 'jupyter', label: 'Jupyter notebook analysis', kind: 'optional', toolPrefix: ['jupyter_'] },
      { id: 'docs-export', label: 'Docs / Sheets export', kind: 'optional', toolPrefix: ['docs_', 'sheets_'], tools: ['export_to_google_docs'] },
    ],
  },
  {
    name: 'Output',
    items: [
      { id: 'commit', label: 'Commit research block', kind: 'core', tools: ['commit_research_block'] },
      { id: 'write-report', label: 'Write report', kind: 'required', tools: ['set_report', 'append_report', 'update_report_section', 'insert_report_section'] },
      { id: 'declare-gaps', label: 'Declare gaps', kind: 'required', signal: 'gaps' },
    ],
  },
];

const TOTAL_ITEMS = GROUPS.reduce((n, g) => n + g.items.length, 0);

interface Signals {
  tools: Set<string>;
  phases: Set<string>;
  coverage: boolean;
  gaps: boolean;
  markers: boolean;
  nodes: boolean;
}

function matches(item: Item, s: Signals): boolean {
  if (item.tools?.some((t) => s.tools.has(t))) return true;
  if (item.toolPrefix?.some((p) => Array.from(s.tools).some((t) => t.startsWith(p)))) return true;
  if (item.phases?.some((p) => s.phases.has(p))) return true;
  if (item.signal === 'coverage' && s.coverage) return true;
  if (item.signal === 'gaps' && s.gaps) return true;
  if (item.signal === 'markers' && s.markers) return true;
  if (item.signal === 'nodes' && s.nodes) return true;
  return false;
}

function rowState(item: Item, done: boolean, hasActivity: boolean): RowState {
  if (done) return 'done';
  if (item.kind === 'required' && hasActivity) return 'pending';
  return 'available';
}

// ---- icons -----------------------------------------------------------------
const CheckIcon = ({ className = '' }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round" className={className}><polyline points="4 12 9 17 20 6" /></svg>
);
const AlertIcon = ({ className = '' }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" className={className}><circle cx="12" cy="12" r="9" /><path d="M12 8v5" /><path d="M12 16h.01" /></svg>
);
const RingIcon = ({ className = '' }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className={className}><circle cx="12" cy="12" r="8" /></svg>
);
const TriangleIcon = ({ className = '' }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M12 9v4" /><path d="M12 17h.01" /><path d="M10.3 4.3 2.6 18a2 2 0 0 0 1.7 3h15.4a2 2 0 0 0 1.7-3L13.7 4.3a2 2 0 0 0-3.4 0Z" /></svg>
);

export default function ResearchActivityChecklist() {
  const [open, setOpen] = useState(false);
  const events = useProjectStore((s) => s.firestoreEvents);
  const dictWorkspace = useProjectStore((s) => s.dictWorkspace);

  const signals = useMemo<Signals>(() => {
    const tools = new Set<string>();
    const phases = new Set<string>();
    let coverage = false;
    let gaps = false;

    for (const e of events as ChatEventFirestore[]) {
      if (e.type === 'tool_call' && e.content) {
        tools.add(e.content.trim());
      } else if (e.type === 'research_progress' && e.content) {
        try {
          const d = JSON.parse(e.content);
          if (Array.isArray(d.phases)) {
            for (const p of d.phases) if (p?.phase) phases.add(String(p.phase));
          }
          if (d.currentPhase) phases.add(String(d.currentPhase));
          if (d.coverage) coverage = true;
          if (Array.isArray(d.gaps) && d.gaps.length > 0) gaps = true;
        } catch {
          /* malformed progress payload — ignore */
        }
      }
    }

    // Findings also record which tool sourced them.
    const findings = dictWorkspace?.findings ?? {};
    for (const f of Object.values(findings)) {
      if (f?.tool_used) tools.add(String(f.tool_used).trim());
    }

    const markers = Object.keys(dictWorkspace?.markers ?? {}).length > 0;
    const nodes = Object.keys(dictWorkspace?.nodes ?? {}).length > 0;
    return { tools, phases, coverage, gaps, markers, nodes };
  }, [events, dictWorkspace]);

  const computed = useMemo(() => {
    const hasActivity = signals.tools.size > 0 || signals.phases.size > 0 || signals.markers || signals.nodes;
    let doneCount = 0;
    let pendingCount = 0;
    const groups = GROUPS.map((g) => {
      let groupDone = 0;
      const items = g.items.map((item) => {
        const done = matches(item, signals);
        const state = rowState(item, done, hasActivity);
        if (state === 'done') { doneCount++; groupDone++; }
        if (state === 'pending') pendingCount++;
        return { ...item, state };
      });
      return { name: g.name, items, frac: `${groupDone} / ${g.items.length}` };
    });
    return { groups, doneCount, pendingCount, hasActivity };
  }, [signals]);

  const heroChips = useMemo(() => {
    const done = (id: string) => computed.groups.some((g) => g.items.some((i) => i.id === id && i.state === 'done'));
    const chips: string[] = [];
    if (done('deep-research')) chips.push('Deep research');
    if (done('light-research')) chips.push('Light research');
    if (computed.groups.some((g) => g.items.some((i) => i.id === 'structuring' && i.state === 'done')) || signals.nodes) chips.push('Graph build');
    if (done('commit')) chips.push('Committed');
    return chips;
  }, [computed, signals.nodes]);

  const notValidated = computed.pendingCount > 0;

  // ---- collapsed launcher — tiny unassuming button at the minimap's bottom-left
  if (!open) {
    return (
      <div className="group absolute bottom-[15px] right-[220px] z-20">
        <button
          onClick={() => setOpen(true)}
          aria-label="Research activity"
          className="w-[18px] h-[18px] flex items-center justify-center rounded bg-surface border border-border-default shadow-bark-xs text-fg-tertiary hover:bg-bg-warm hover:text-fg-secondary hover:border-border-strong transition-colors"
        >
          <CheckIcon className="w-3 h-3" />
        </button>
        {/* hover tooltip — opens upward (button sits at the canvas bottom) */}
        <div className="pointer-events-none absolute bottom-full right-0 mb-1.5 hidden group-hover:block">
          <div className="px-2.5 py-1.5 rounded-md bg-primary text-on-primary shadow-bark-md whitespace-nowrap">
            <div className="text-xs font-semibold">Research activity</div>
            <div className="text-[11px] opacity-70">
              {computed.doneCount}/{TOTAL_ITEMS} used{notValidated ? ` · ${computed.pendingCount} to do` : ''}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---- expanded panel ------------------------------------------------------
  return (
    <aside
      aria-label="Research activity"
      className="absolute bottom-4 right-4 z-30 w-[348px] max-h-[calc(100%-2rem)] flex flex-col bg-surface border border-border-default rounded-xl shadow-bark-lg overflow-hidden"
    >
      {/* header */}
      <div className="px-4 pt-4 pb-3 border-b border-border-default/60">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="text-[17px] font-semibold text-fg flex items-center">
              <span className="inline-block w-[7px] h-[7px] rounded-full bg-secondary mr-2" />
              Research activity
            </div>
            <div className="text-xs text-fg-tertiary mt-0.5">What this project has used so far</div>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-[11px] font-semibold text-fg-secondary bg-bg-warm border border-border-default/60 rounded-full px-2 py-0.5 whitespace-nowrap">
              {computed.doneCount} / {TOTAL_ITEMS}
            </span>
            <button
              onClick={() => setOpen(false)}
              aria-label="Collapse"
              className="w-[26px] h-[26px] rounded-md flex items-center justify-center text-fg-tertiary hover:bg-bg-warm hover:text-fg transition-colors"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" className="w-[15px] h-[15px]"><path d="M18 6 6 18M6 6l12 12" /></svg>
            </button>
          </div>
        </div>
      </div>

      {/* hero — used this session */}
      <div className="px-4 pt-3 pb-4 border-b border-border-default/60 bg-surface-sunken">
        <div className="text-[11px] font-semibold tracking-[0.08em] uppercase text-fg-tertiary mb-2">Used this session</div>
        <div className="flex flex-wrap gap-2">
          {heroChips.length === 0 && !notValidated && (
            <span className="text-xs text-fg-tertiary">No research activity yet.</span>
          )}
          {heroChips.map((c) => (
            <span key={c} className="inline-flex items-center gap-1.5 h-6 pl-1.5 pr-2 rounded bg-primary text-on-primary text-xs font-semibold">
              <CheckIcon className="w-3 h-3" />
              {c}
            </span>
          ))}
          {notValidated && (
            <span className="inline-flex items-center gap-1.5 h-6 pl-1.5 pr-2 rounded bg-warning-bg text-warning text-xs font-semibold">
              <TriangleIcon className="w-3 h-3" />
              Not validated yet
            </span>
          )}
        </div>
      </div>

      {/* grouped checklist — all groups open */}
      <div className="overflow-y-auto py-2">
        {computed.groups.map((g) => (
          <div key={g.name} className="border-b border-border-default/60 last:border-b-0">
            <div className="flex items-center justify-between px-4 py-3">
              <span className="text-sm font-semibold text-fg">{g.name}</span>
              <span className="font-mono text-[11px] text-fg-tertiary">{g.frac}</span>
            </div>
            <div className="pb-2">
              {g.items.map((item) => (
                <div key={item.id} className="flex items-center gap-3 pl-6 pr-4 py-[7px]">
                  <span className="flex-none w-4 h-4 flex items-center justify-center">
                    {item.state === 'done' && <CheckIcon className="w-4 h-4 text-success" />}
                    {item.state === 'pending' && <AlertIcon className="w-4 h-4 text-warning" />}
                    {item.state === 'available' && <RingIcon className="w-4 h-4 text-border-strong" />}
                  </span>
                  <span className={`text-sm flex-1 ${item.state === 'available' ? 'text-fg-tertiary' : 'text-fg'}`}>{item.label}</span>
                  {item.state !== 'done' && item.kind !== 'core' && (
                    <span
                      className={`text-[10px] font-semibold tracking-wide uppercase px-1.5 py-px rounded-sm ${
                        item.state === 'pending' ? 'text-warning bg-warning-bg' : 'text-fg-tertiary bg-bg-warm'
                      }`}
                    >
                      {item.state === 'pending' ? 'to do' : item.kind}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* legend */}
      <div className="flex gap-4 px-4 py-3 border-t border-border-default/60 bg-surface-sunken">
        <span className="flex items-center gap-1.5 text-xs text-fg-tertiary"><CheckIcon className="w-3.5 h-3.5 text-success" /> used</span>
        <span className="flex items-center gap-1.5 text-xs text-fg-tertiary"><AlertIcon className="w-3.5 h-3.5 text-warning" /> to do</span>
        <span className="flex items-center gap-1.5 text-xs text-fg-tertiary"><RingIcon className="w-3.5 h-3.5 text-border-strong" /> available</span>
      </div>
    </aside>
  );
}
